import { classifyChange, type ChangeClassification } from '../decisions/change-policy.js';
import { e1rmEpleyRir } from './e1rm.js';
import type {
  ExerciseHistory,
  ExerciseSessionSummary,
  ProgressionState,
  ProgressionStrategy,
  RepTarget,
} from './types.js';

/**
 * Estancamiento de rendimiento (docs/TRAINING_ENGINE.md §9).
 *
 * El campo `stall_counter` existe en la base de datos desde la migracion 0003 y
 * hasta ahora nadie lo incrementaba: valia 0 siempre. Esto es lo que le da
 * valor.
 *
 * La regla que mas cuesta respetar es la segunda condicion de deteccion: si el
 * RIR real ha sido sistematicamente MAYOR que el programado, NO es
 * estancamiento. Es que se esta entrenando por debajo de lo prescrito, y
 * responder a eso bajando la carga o cambiando el ejercicio empeora justo lo
 * que falla.
 */

// ---------------------------------------------------------------------------
// Umbrales
// ---------------------------------------------------------------------------

/** Sesiones consecutivas del mismo ejercicio sin mejora (§9). */
export const STALL_SESSIONS = 3;

/**
 * Cuanto RIR de sobra, de media, descarta el estancamiento.
 *
 * CONVENCION declarada: el documento dice «sistematicamente superior» sin
 * cuantificarlo. Medio punto de RIR es el escalon mas pequeño que el usuario
 * registra, asi que exigir al menos eso evita que el redondeo decida.
 */
export const STALL_RIR_SLACK = 0.5;

/** Patrones principales estancados a la vez que fuerzan revision (§9). */
export const STALL_SIMULTANEOUS_PATTERNS = 3;

/** Recorte de carga de la reduccion tecnica del escalon 3 (§9). */
export const TECHNICAL_DELOAD_FACTOR = 0.9;

/** Sesiones que hay que reconstruir tras la reduccion tecnica (§9). */
export const TECHNICAL_DELOAD_SESSIONS = 2;

/**
 * Estrategia a la que se pasa cuando la actual deja de progresar (§9 escalon 2).
 *
 * CONVENCION declarada. El documento solo da el ejemplo «doble progresion →
 * top set + back-off»; el resto sigue la misma idea: cambiar la variable con la
 * que se progresa, no insistir en la misma con otro numero.
 */
export const NEXT_STRATEGY: Record<ProgressionStrategy, ProgressionStrategy> = {
  double_progression: 'top_backoff',
  load_progression: 'double_progression',
  rep_progression: 'double_progression',
  top_backoff: 'rir_ramp',
  rir_ramp: 'double_progression',
};

// ---------------------------------------------------------------------------
// Deteccion
// ---------------------------------------------------------------------------

function mejorE1rm(session: ExerciseSessionSummary): number {
  return session.sets.reduce((max, set) => Math.max(max, e1rmEpleyRir(set)), 0);
}

function cargaMasAlta(session: ExerciseSessionSummary): number {
  return session.sets.reduce((max, set) => Math.max(max, set.loadKg), 0);
}

/** Repeticiones totales hechas a la carga mas alta de la sesion. */
function repsALaCargaAlta(session: ExerciseSessionSummary): number {
  const carga = cargaMasAlta(session);
  return session.sets
    .filter((set) => set.loadKg === carga)
    .reduce((total, set) => total + set.reps, 0);
}

function rirMedio(session: ExerciseSessionSummary): number | null {
  if (session.sets.length === 0) return null;
  return session.sets.reduce((total, set) => total + set.rir, 0) / session.sets.length;
}

export interface StallDetection {
  stalled: boolean;
  reasonCodes: string[];
}

/**
 * ¿Lleva el ejercicio `STALL_SESSIONS` sesiones sin moverse?
 *
 * Se exige que fallen las DOS vias de progreso —e1RM y repeticiones a la misma
 * carga— porque cualquiera de las dos por si sola es progreso. Subir una
 * repeticion con la misma carga no mueve mucho el e1RM estimado y seguiria
 * pareciendo estancamiento si solo se mirara ese numero.
 */
export function detectStall(history: ExerciseHistory, target: RepTarget): StallDetection {
  const sesiones = history.sessions.filter((session) => session.sets.length > 0);
  if (sesiones.length < STALL_SESSIONS) {
    return { stalled: false, reasonCodes: ['not_enough_sessions'] };
  }

  const ventana = sesiones.slice(-STALL_SESSIONS);
  const primera = ventana[0] as ExerciseSessionSummary;
  const ultima = ventana[ventana.length - 1] as ExerciseSessionSummary;

  const e1rmMejora = mejorE1rm(ultima) > mejorE1rm(primera);
  const repsMejoran =
    cargaMasAlta(ultima) >= cargaMasAlta(primera) &&
    repsALaCargaAlta(ultima) > repsALaCargaAlta(primera);

  if (e1rmMejora || repsMejoran) {
    return { stalled: false, reasonCodes: ['still_progressing'] };
  }

  // La comprobacion que evita el peor error de este modulo: entrenar por debajo
  // de lo prescrito NO es estancarse, y tratarlo como tal agrava el problema.
  const holgura = ventana
    .map(rirMedio)
    .filter((rir): rir is number => rir !== null)
    .map((rir) => rir - target.rir);
  const entrenandoFlojo =
    holgura.length === ventana.length && holgura.every((margen) => margen >= STALL_RIR_SLACK);

  if (entrenandoFlojo) {
    return { stalled: false, reasonCodes: ['rir_systematically_above_target'] };
  }

  return { stalled: true, reasonCodes: ['no_progress_in_three_sessions'] };
}

/**
 * Escalon de estancamiento DERIVADO del historial: ventanas consecutivas, de la
 * mas reciente hacia atras, en las que el ejercicio estaba parado.
 *
 * Es una funcion pura del historial, asi que da lo mismo cuantas veces se
 * llame. Es la diferencia entre un contador que refleja lo que paso en el
 * gimnasio y uno que refleja cuantas veces se abrio el panel de propuestas.
 *
 * `cutoffs` son los finales de cada ventana (fechas locales `YYYY-MM-DD`),
 * ordenados de mas antigua a mas reciente. Quien llama decide la cadencia: el
 * §9 dice cuando hay estancamiento, no cada cuanto sube el escalon.
 */
export function stallCounterFromHistory(
  history: ExerciseHistory,
  target: RepTarget,
  cutoffs: readonly string[],
): number {
  let seguidas = 0;
  for (const corte of [...cutoffs].reverse()) {
    const hasta: ExerciseHistory = {
      // `session.localDate`, no `session.at.slice(0, 10)`. El segundo da el dia
      // en UTC, y `corte` es una fecha LOCAL: entre medianoche y el desfase del
      // huso comparaba dias de calendarios distintos y sacaba de la ventana una
      // sesion que si estaba dentro (NEW-001). El dia local llega ya resuelto
      // desde la frontera, que es lo unico compatible con ADR-007.
      sessions: history.sessions.filter((session) => session.localDate < corte),
    };
    if (!detectStall(hasta, target).stalled) break;
    seguidas += 1;
  }
  return seguidas;
}

// ---------------------------------------------------------------------------
// Escalado de respuesta
// ---------------------------------------------------------------------------

export type StallAction =
  | 'none'
  | 'change_strategy'
  | 'technical_deload'
  | 'substitute_exercise'
  | 'structural_review';

export interface StallInput {
  history: ExerciseHistory;
  state: ProgressionState;
  target: RepTarget;
  /**
   * Escalon alcanzado, YA RESUELTO por quien llama.
   *
   * No se deriva de `state.stallCounter + 1` a proposito. Si esta funcion
   * acumulara, llamarla dos veces con los mismos datos daria dos escalones
   * distintos, y el resultado dependeria de cuantas veces se abrio una
   * pantalla en lugar de lo que paso en el gimnasio. Usa
   * `stallCounterFromHistory` para calcularlo de forma reproducible.
   */
  stallCounter: number;
  /**
   * Deficit calorico agresivo en curso. Cadencia no prescribe nutricion desde
   * el 21/09/2026, asi que hoy nadie lo sabe y llega `null`: sin dato no se
   * supone que lo haya, pero tampoco que no. La rama se conserva porque el
   * cruce es correcto si algun dia el atleta lo declara.
   */
  aggressiveDeficit?: boolean | null | undefined;
  /** Patrones principales estancados a la vez, INCLUIDO este. */
  simultaneousStalledPatterns?: number | undefined;
}

export interface StallAssessment {
  stalled: boolean;
  /** Valor que debe guardarse en `progression_state.stall_counter`. */
  stallCounter: number;
  action: StallAction;
  /** Estrategia nueva, solo cuando `action` es `change_strategy`. */
  nextStrategy: ProgressionStrategy | null;
  /** Factor de carga a aplicar (1 = sin tocar). */
  loadFactor: number;
  /** Sesiones a reconstruir tras la reduccion tecnica. */
  rebuildSessions: number;
  reasonCodes: string[];
  /** `null` cuando no hay nada que decidir. */
  change: ChangeClassification | null;
}

export function assessStall(input: StallInput): StallAssessment {
  const deteccion = detectStall(input.history, input.target);

  if (!deteccion.stalled) {
    // El contador vuelve a cero al progresar. No lo dice el documento; se
    // declara aqui: un contador que solo sube acabaria disparando una revision
    // estructural por tres sesiones malas repartidas en medio año.
    return {
      stalled: false,
      stallCounter: 0,
      action: 'none',
      nextStrategy: null,
      loadFactor: 1,
      rebuildSessions: 0,
      reasonCodes: [...deteccion.reasonCodes, 'stall_counter_reset'],
      change: null,
    };
  }

  const contador = Math.max(1, Math.trunc(input.stallCounter));
  const reasonCodes = [...deteccion.reasonCodes];

  const simultaneos = input.simultaneousStalledPatterns ?? 1;
  const enVariosPatrones = simultaneos >= STALL_SIMULTANEOUS_PATTERNS;
  if (enVariosPatrones) reasonCodes.push('simultaneous_stall_in_main_patterns');

  /**
   * Estancarse en tres patrones a la vez pesa al menos tanto como el escalon
   * mas alto de un solo ejercicio: si falla todo, el problema no esta en el
   * ejercicio.
   */
  const efectivo = enVariosPatrones ? Math.max(contador, 5) : contador;

  // Cruce con nutricion (§9): un estancamiento en deficit agresivo no se arregla
  // cambiando de ejercicio, porque lo que falta es energia. Se sube directo a
  // revision para que la respuesta salga por el lado que corresponde.
  const porDeficit = input.aggressiveDeficit === true && efectivo >= 3;
  if (porDeficit) reasonCodes.push('stall_under_aggressive_deficit');

  const escalon = porDeficit ? 5 : efectivo;
  const base = {
    stalled: true,
    stallCounter: contador,
    nextStrategy: null,
    loadFactor: 1,
    rebuildSessions: 0,
  } as const;

  const clasificar = (): ChangeClassification =>
    classifyChange({ kind: 'stall_response', magnitude: escalon });

  if (escalon <= 1) {
    // Una sesion mala es ruido. Actuar aqui seria perseguir el ruido.
    return { ...base, action: 'none', reasonCodes: [...reasonCodes, 'noise'], change: null };
  }

  if (escalon === 2) {
    return {
      ...base,
      action: 'change_strategy',
      nextStrategy: NEXT_STRATEGY[input.state.strategy],
      reasonCodes: [...reasonCodes, 'change_progression_strategy'],
      change: clasificar(),
    };
  }

  if (escalon === 3) {
    return {
      ...base,
      action: 'technical_deload',
      loadFactor: TECHNICAL_DELOAD_FACTOR,
      rebuildSessions: TECHNICAL_DELOAD_SESSIONS,
      reasonCodes: [...reasonCodes, 'technical_deload'],
      change: clasificar(),
    };
  }

  if (escalon === 4) {
    return {
      ...base,
      action: 'substitute_exercise',
      reasonCodes: [...reasonCodes, 'substitute_same_pattern'],
      change: clasificar(),
    };
  }

  return {
    ...base,
    action: 'structural_review',
    reasonCodes: [...reasonCodes, 'evaluate_deload_phase_or_nutrition'],
    change: clasificar(),
  };
}
